import { rawKeyFromNamespaced } from "../apps/appFilterKeys";
import type { Screen0LanguageCode } from "@/features/language/language-registry";
import { friendlyAppName } from "../apps/friendlyAppName";
import { isUnattributedApp } from "../native/mergeSameDaySnapshot";
import { formatDuration, formatInsightTimeBandLabel } from "../format";
import type { Rule, Target } from "../rules/ruleTypes";
import { buildActiveSmartBlockRows } from "./activeSmartBlocks";
import {
  scoreInsightWindow,
  type InsightScores,
  type InsightWindowDay,
  type InsightWindowResult,
} from "./insightScoring";

const BAND_KEYS = ["morning", "afternoon", "evening", "night"] as const;
const MAX_APPS_PER_DAY = 20;
const MAX_CONTEXT_JSON_CHARS = 48_000;
/** On-device LLM chat budget — much smaller than full insight export. */
export const MAX_CHAT_CONTEXT_JSON_CHARS = 10_000;

export type InsightChatComparison = {
  previousWindow: { startDate: string; endDate: string };
  current: WindowAggregate;
  previous: WindowAggregate;
};

export type WindowAggregate = {
  totalSeconds: number;
  daysWithData: number;
  topApps: Array<{ displayName: string; appIdentifier?: string; seconds: number; opens: number }>;
  timeBands: Record<string, number>;
};

export type InsightChatContextPayload = {
  window: InsightWindowResult;
  excludedApps: {
    keys: string[];
    names: string[];
    note: string;
  };
  scores: {
    status: InsightScores["status"];
    daysWithData: number;
    totalSeconds: number;
    hasOpenSignal: boolean;
    compulsivityTop: Array<{
      displayName: string;
      appIdentifier?: string;
      score: number;
      totalSeconds: number;
      opens: number;
    }>;
    doomScrollRiskTop: Array<{
      displayName: string;
      appIdentifier?: string;
      score: number;
      totalSeconds: number;
      opens: number;
    }>;
    suggestions: Array<{
      displayName: string;
      appIdentifier?: string;
      goal: string;
      startHour: number;
      endHour: number;
      reason: string;
    }>;
  };
  timeBands: {
    aggregated: Record<string, number>;
    perDay: Array<{ date: string; bands: Record<string, number> }>;
  };
  smartBlocks: {
    active: Array<{
      displayName: string;
      appIdentifier?: string;
      goal?: string;
      windowLabel: string;
      score: number;
    }>;
    suggested: Array<{
      displayName: string;
      appIdentifier?: string;
      goal: string;
      startHour: number;
      endHour: number;
      reason: string;
    }>;
  };
  rules: Array<{
    id: string;
    enabled: boolean;
    source?: string;
    smartBlockGoal?: string;
    targetCount: number;
    window: { startMinute: number; durationMinutes: number };
  }>;
  targets: Array<{
    id: string;
    name: string;
    apps: Array<{ displayName: string; appIdentifier?: string }>;
  }>;
  comparison: InsightChatComparison;
};

type SmartBlockLabels = { goalCompulsivity: string; goalDoom: string };

function usableDays(days: InsightWindowDay[]): InsightWindowDay[] {
  return days
    .filter(
      (day) =>
        day.permissionStatus === "granted" &&
        day.collectionStatus !== "missing_permission" &&
        day.collectionStatus !== "metric_unsupported" &&
        day.topApps.some((app) => app.durationSeconds > 0),
    )
    .sort((a, b) => a.date.localeCompare(b.date));
}

function trimDayApps(day: InsightWindowDay): InsightWindowDay {
  const topApps = day.topApps
    .filter((app) => !isUnattributedApp(app))
    .sort((a, b) => b.durationSeconds - a.durationSeconds)
    .slice(0, MAX_APPS_PER_DAY);
  return { ...day, topApps };
}

function aggregateWindow(days: InsightWindowDay[]): WindowAggregate {
  const usable = usableDays(days);
  const appTotals = new Map<string, { displayName: string; appIdentifier?: string; seconds: number; opens: number }>();
  const timeBands: Record<string, number> = { morning: 0, afternoon: 0, evening: 0, night: 0 };
  let totalSeconds = 0;

  for (const day of usable) {
    const dayTopSeconds = day.topApps.reduce((sum, app) => sum + Math.max(0, app.durationSeconds), 0);
    totalSeconds += day.totalScreenTimeSeconds > 0 ? day.totalScreenTimeSeconds : dayTopSeconds;
    for (const key of BAND_KEYS) {
      timeBands[key] += day.timeBands.find((band) => band.key === key)?.seconds ?? 0;
    }
    for (const app of day.topApps) {
      if (app.durationSeconds <= 0 || isUnattributedApp(app)) continue;
      const key = app.appIdentifier?.trim() || app.displayName.trim();
      const existing = appTotals.get(key) ?? {
        displayName: app.displayName,
        appIdentifier: app.appIdentifier,
        seconds: 0,
        opens: 0,
      };
      existing.seconds += app.durationSeconds;
      existing.opens += app.opens ?? 0;
      appTotals.set(key, existing);
    }
  }

  const topApps = [...appTotals.values()]
    .sort((a, b) => b.seconds - a.seconds || b.opens - a.opens)
    .slice(0, 10);

  return {
    totalSeconds,
    daysWithData: usable.length,
    topApps,
    timeBands,
  };
}

function metricSummary(metrics: InsightScores["compulsivityTop"]) {
  return metrics.map((metric) => ({
    displayName: friendlyAppName(metric),
    appIdentifier: metric.appIdentifier,
    score: metric.score,
    totalSeconds: metric.totalSeconds,
    opens: metric.opens,
  }));
}

export function buildInsightChatContext(params: {
  current: InsightWindowResult;
  previous: InsightWindowResult;
  rules: Rule[];
  targets: Target[];
  excludedAppKeys: string[];
  smartBlockLabels: SmartBlockLabels;
  languageCode?: Screen0LanguageCode;
}): InsightChatContextPayload {
  const { current, previous, rules, targets, excludedAppKeys, smartBlockLabels, languageCode = "en" } = params;
  const scores = scoreInsightWindow(current, { rules, targets, excludedAppKeys, languageCode });
  const activeRows = buildActiveSmartBlockRows(
    rules,
    targets,
    scores.compulsivityRanked,
    scores.doomScrollRiskRanked,
    smartBlockLabels,
    languageCode,
  );

  const excludedNames = excludedAppKeys
    .map((key) => rawKeyFromNamespaced(key))
    .filter(Boolean);

  const aggregatedBands: Record<string, number> = { morning: 0, afternoon: 0, evening: 0, night: 0 };
  const perDayBands: Array<{ date: string; bands: Record<string, number> }> = [];
  for (const day of current.days) {
    const bands: Record<string, number> = {};
    for (const key of BAND_KEYS) {
      const seconds = day.timeBands.find((band) => band.key === key)?.seconds ?? 0;
      bands[key] = seconds;
      aggregatedBands[key] += seconds;
    }
    perDayBands.push({ date: day.date, bands });
  }

  const trimmedWindow: InsightWindowResult = {
    ...current,
    days: current.days.map(trimDayApps),
  };

  return {
    window: trimmedWindow,
    excludedApps: {
      keys: excludedAppKeys,
      names: excludedNames,
      note:
        "Excluded apps are omitted from Insight scores and suggestions but remain in raw per-day app lists.",
    },
    scores: {
      status: scores.status,
      daysWithData: scores.daysWithData,
      totalSeconds: scores.totalSeconds,
      hasOpenSignal: scores.hasOpenSignal,
      compulsivityTop: metricSummary(scores.compulsivityTop),
      doomScrollRiskTop: metricSummary(scores.doomScrollRiskTop),
      suggestions: scores.suggestions.map((suggestion) => ({
        displayName: friendlyAppName(suggestion),
        appIdentifier: suggestion.appIdentifier,
        goal: suggestion.goal,
        startHour: suggestion.startHour,
        endHour: suggestion.endHour,
        reason: suggestion.reason,
      })),
    },
    timeBands: {
      aggregated: aggregatedBands,
      perDay: perDayBands,
    },
    smartBlocks: {
      active: activeRows.map((row) => ({
        displayName: friendlyAppName(row),
        appIdentifier: row.appIdentifier,
        goal: row.goal,
        windowLabel: row.windowLabel,
        score: row.score,
      })),
      suggested: scores.suggestions.map((suggestion) => ({
        displayName: friendlyAppName(suggestion),
        appIdentifier: suggestion.appIdentifier,
        goal: suggestion.goal,
        startHour: suggestion.startHour,
        endHour: suggestion.endHour,
        reason: suggestion.reason,
      })),
    },
    rules: rules.map((rule) => ({
      id: rule._id,
      enabled: rule.enabled,
      source: rule.source,
      smartBlockGoal: rule.smartBlockGoal,
      targetCount: rule.targetIds.length,
      window: rule.window,
    })),
    targets: targets.map((target) => ({
      id: target._id,
      name: target.name,
      apps: target.apps.map((app) => ({
        displayName: app.label,
        appIdentifier: app.appIdentifier,
      })),
    })),
    comparison: {
      previousWindow: {
        startDate: previous.window.startDate,
        endDate: previous.window.endDate,
      },
      current: aggregateWindow(current.days),
      previous: aggregateWindow(previous.days),
    },
  };
}

export type InsightChatFormattedWindowAggregate = {
  totalDuration: string;
  daysWithData: number;
  topApps: Array<{ displayName: string; appIdentifier?: string; duration: string; opens: number }>;
  timeBands: Record<string, string>;
};

export type InsightChatInferenceContext = {
  window: { startDate: string; endDate: string; daysRequested: number };
  scores: {
    status: InsightChatContextPayload["scores"]["status"];
    daysWithData: number;
    totalDuration: string;
    hasOpenSignal: boolean;
    compulsivityTop: Array<{
      displayName: string;
      appIdentifier?: string;
      score: number;
      duration: string;
      opens: number;
    }>;
    doomScrollRiskTop: Array<{
      displayName: string;
      appIdentifier?: string;
      score: number;
      duration: string;
      opens: number;
    }>;
    suggestions: InsightChatContextPayload["scores"]["suggestions"];
  };
  timeBands: { aggregated: Record<string, string> };
  excludedApps: Pick<InsightChatContextPayload["excludedApps"], "names" | "note">;
  comparison: {
    previousWindow: InsightChatComparison["previousWindow"];
    current: InsightChatFormattedWindowAggregate;
    previous: InsightChatFormattedWindowAggregate;
  };
  dailySummary: Array<{
    date: string;
    totalDuration: string;
    topApps: Array<{ displayName: string; duration: string; opens: number }>;
  }>;
};

function formatTimeBandsForChat(
  bands: Record<string, number>,
  languageCode: Screen0LanguageCode,
): Record<string, string> {
  const formatted: Record<string, string> = {};
  for (const key of BAND_KEYS) {
    const label = formatInsightTimeBandLabel(key, languageCode);
    formatted[label] = formatDuration(bands[key] ?? 0, languageCode);
  }
  return formatted;
}

function formatWindowAggregateForChat(
  aggregate: WindowAggregate,
  languageCode: Screen0LanguageCode,
): InsightChatFormattedWindowAggregate {
  return {
    totalDuration: formatDuration(aggregate.totalSeconds, languageCode),
    daysWithData: aggregate.daysWithData,
    topApps: aggregate.topApps.map((app) => ({
      displayName: app.displayName,
      appIdentifier: app.appIdentifier,
      duration: formatDuration(app.seconds, languageCode),
      opens: app.opens,
    })),
    timeBands: formatTimeBandsForChat(aggregate.timeBands, languageCode),
  };
}

function buildInsightChatInferenceContext(
  context: InsightChatContextPayload,
  languageCode: Screen0LanguageCode,
  dailyAppLimit: number,
  dayLimit: number,
): InsightChatInferenceContext {
  const usableDays = context.window.days
    .filter(
      (day) =>
        day.permissionStatus === "granted" &&
        day.collectionStatus !== "missing_permission" &&
        day.collectionStatus !== "metric_unsupported",
    )
    .slice(-dayLimit);

  return {
    window: {
      startDate: context.window.window.startDate,
      endDate: context.window.window.endDate,
      daysRequested: context.window.window.daysRequested,
    },
    scores: {
      status: context.scores.status,
      daysWithData: context.scores.daysWithData,
      totalDuration: formatDuration(context.scores.totalSeconds, languageCode),
      hasOpenSignal: context.scores.hasOpenSignal,
      compulsivityTop: context.scores.compulsivityTop.slice(0, 5).map((metric) => ({
        displayName: metric.displayName,
        appIdentifier: metric.appIdentifier,
        score: metric.score,
        duration: formatDuration(metric.totalSeconds, languageCode),
        opens: metric.opens,
      })),
      doomScrollRiskTop: context.scores.doomScrollRiskTop.slice(0, 5).map((metric) => ({
        displayName: metric.displayName,
        appIdentifier: metric.appIdentifier,
        score: metric.score,
        duration: formatDuration(metric.totalSeconds, languageCode),
        opens: metric.opens,
      })),
      suggestions: context.scores.suggestions.slice(0, 3),
    },
    timeBands: {
      aggregated: formatTimeBandsForChat(context.timeBands.aggregated, languageCode),
    },
    excludedApps: {
      names: context.excludedApps.names,
      note: context.excludedApps.note,
    },
    comparison: {
      previousWindow: context.comparison.previousWindow,
      current: formatWindowAggregateForChat(context.comparison.current, languageCode),
      previous: formatWindowAggregateForChat(context.comparison.previous, languageCode),
    },
    dailySummary: usableDays.map((day) => ({
      date: day.date,
      totalDuration: formatDuration(day.totalScreenTimeSeconds, languageCode),
      topApps: day.topApps
        .filter((app) => !isUnattributedApp(app))
        .sort((a, b) => b.durationSeconds - a.durationSeconds)
        .slice(0, dailyAppLimit)
        .map((app) => ({
          displayName: app.displayName,
          duration: formatDuration(app.durationSeconds, languageCode),
          opens: app.opens ?? 0,
        })),
    })),
  };
}

/** Slim context for on-device chat — omits rules, targets, smart blocks, and heavy per-day lists. */
export function serializeInsightChatContextForChat(
  context: InsightChatContextPayload,
  languageCode: Screen0LanguageCode,
): string {
  const attempts: Array<{ dailyAppLimit: number; dayLimit: number }> = [
    { dailyAppLimit: 3, dayLimit: 14 },
    { dailyAppLimit: 2, dayLimit: 10 },
    { dailyAppLimit: 1, dayLimit: 7 },
  ];

  for (const { dailyAppLimit, dayLimit } of attempts) {
    const json = JSON.stringify(
      buildInsightChatInferenceContext(context, languageCode, dailyAppLimit, dayLimit),
    );
    if (json.length <= MAX_CHAT_CONTEXT_JSON_CHARS) return json;
  }

  const minimal = buildInsightChatInferenceContext(context, languageCode, 0, 7);
  return JSON.stringify({
    window: minimal.window,
    scores: {
      status: minimal.scores.status,
      daysWithData: minimal.scores.daysWithData,
      totalDuration: minimal.scores.totalDuration,
      hasOpenSignal: minimal.scores.hasOpenSignal,
      compulsivityTop: minimal.scores.compulsivityTop.slice(0, 3),
      doomScrollRiskTop: minimal.scores.doomScrollRiskTop.slice(0, 3),
      suggestions: [],
    },
    timeBands: minimal.timeBands,
    excludedApps: minimal.excludedApps,
    comparison: minimal.comparison,
    _truncated: true,
  });
}

export function serializeInsightChatContext(context: InsightChatContextPayload): string {
  let json = JSON.stringify(context);
  if (json.length <= MAX_CONTEXT_JSON_CHARS) return json;

  const reduced: InsightChatContextPayload = {
    ...context,
    window: {
      ...context.window,
      days: context.window.days.slice(-10).map((day) => ({
        ...day,
        topApps: day.topApps.slice(0, 12),
      })),
    },
    targets: context.targets.slice(0, 20),
    rules: context.rules.slice(0, 20),
  };
  json = JSON.stringify(reduced);
  if (json.length <= MAX_CONTEXT_JSON_CHARS) return json;

  return JSON.stringify({
    ...reduced,
    window: {
      ...reduced.window,
      days: reduced.window.days.slice(-7),
    },
    _truncated: true,
    _note: "Context trimmed for on-device model limits; totals and comparison kept.",
  });
}

export function hasInsightChatUsageData(scores: InsightScores | null): boolean {
  if (!scores) return false;
  if (scores.status === "missing_permission" || scores.status === "no_data") return false;
  return scores.daysWithData > 0 && scores.totalSeconds > 0;
}

export function hasInsightChatContextUsageData(context: InsightChatContextPayload): boolean {
  return hasInsightChatUsageData({
    status: context.scores.status,
    daysWithData: context.scores.daysWithData,
    totalSeconds: context.scores.totalSeconds,
  } as InsightScores);
}
